import { useState, useRef, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { InputUSD } from '../../ui/input-usd'
import { Label } from '../../ui/label'
import { Textarea } from '../../ui/textarea'
import {
  Plus,
  Trash2,
  Copy,
  ArrowLeft,
  ArrowRight,
  AlertCircle,
  InfoIcon,
  ExternalLink,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip'
import type { DealFormData, FlexibleSchedule } from '../../../lib/types'
import {
  DISTRIBUTION_CHAINS,
  DEPOSIT_CHAIN_STABLECOINS,
} from '../../../lib/constants'
import { DropdownSelect } from '../../ui/dropdown-select'
import {
  parseReferralCsvFile,
  parseWhitelistCsvFile,
} from '../../../lib/parsers'
import { validateInvestmentTerms } from 'submission/lib/step-validators'
import { FileUpload } from '../file-upload'
import { cn } from '../../../lib/utils'
import { OverviewWhitelistSection, BuybackProtectionSection } from './overview'

const CUSTOM_REFERRAL_TOOLTIP_LINES = [
  `When a promo code is used the associated kickback is split between T2 (KOL or partner who shared the code) and T3 (higher-lvl ref, e.g.: PR Agency). Use the T2 Share and T3 Share columns to define the percentage split of the total kickback.`,
  `T2 Addresses can have a T3 partner, in that case they report the T3 address in the T3 Wallet Address column. If the T2 Address isn't sharing the kickback leave T3 Wallet Address blank.`,
  `T3 Addresses can map to multiple T2 addresses, only share the T3 Address in the T3 Wallet Address column. Never fill T2 Wallet Address for a T3 Address, cause the mapping will happen from the T2 Addresses.`,
  `Template example: Jane Doe (T2) shares a promo code that gives 5% discount to users. She receives 10% kickback keeping 70% and sharing 30% with the T3 wallet (her PR Agency).`,
] as const

interface StepInvestmentTermsProps {
  formData: DealFormData
  onComplete: (data: Partial<DealFormData>) => void
  onUpdate: (data: Partial<DealFormData>) => void
  onBack: () => void
  headingId?: string
  onValidationError?: (errorMessage: string) => void
  onValidationErrors?: (errors: { id: string; label: string }[]) => void
  requestChanges?: Record<string, string>
}

export default function StepInvestmentTerms({
  formData,
  onComplete,
  onUpdate,
  onBack,
  headingId,
  onValidationError,
  onValidationErrors,
  requestChanges,
}: StepInvestmentTermsProps) {
  const isFullTgeRelease = (
    value: string | number | null | undefined,
  ): boolean => {
    if (value === null || value === undefined || value === '') return false
    const parsed = Number.parseFloat(String(value))
    return !isNaN(parsed) && parsed === 100
  }

  const [localData, setLocalData] = useState(() => {
    const initialFlexibleSchedules = (
      (formData.flexibleSchedules || []) as FlexibleSchedule[]
    ).map((schedule) => {
      const nextSchedule = { ...schedule }
      if (isFullTgeRelease(nextSchedule.tgeRelease)) {
        nextSchedule.cliff = '0'
        nextSchedule.linearVesting = '0'
      }
      return nextSchedule
    })

    const initialState = {
      salesType: (formData as any).salesType || '',
      pricingMethod: formData.pricingMethod || 'fixed',
      fdv: formData.fdv || '',
      tokenPrice: formData.tokenPrice || '',
      tokenSupply: formData.tokenSupply || '',
      totalAllocation: formData.totalAllocation || '',
      minimumRaiseThreshold: formData.minimumRaiseThreshold || '',
      maxRaise: formData.maxRaise || '',
      minimumInvestmentPerUser: formData.minimumInvestmentPerUser || '',
      fixedPoolName: formData.fixedPoolName || 'Genesis Pool',
      cliff: formData.cliff || '',
      tgeRelease: formData.tgeRelease || '',
      linearVesting: formData.linearVesting || '',
      enableBuybackProtection:
        (formData as any).enableBuybackProtection || false,
      buybackPaymentToken: (formData as any).buybackPaymentToken || '',
      buybackPrice: (formData as any).buybackPrice || '',
      fundingWallet: (formData as any).fundingWallet || '',
      buybackStartDate: (formData as any).buybackStartDate || '',
      buybackEndDate: (formData as any).buybackEndDate || '',
      flexibleSchedules: initialFlexibleSchedules,
      whitelistEntries: formData.whitelistEntries || [],
      enableWhitelistAccess: (formData as any).enableWhitelistAccess || false,
      whitelistCsv: (formData as any).whitelistCsv || null,
      whitelistAddressCount: formData.whitelistAddressCount || 0,
      bespokeDescription: (formData as any).bespokeDescription || '',
      countryRestrictions: formData.countryRestrictions || [],
      referralEnabled: formData.referralEnabled || false,
      referralDiscountT1: formData.referralDiscountT1 || '',
      referralDiscountT2: formData.referralDiscountT2 || '',
      referralDiscountT3: formData.referralDiscountT3 || '',
      referralKickbackT1: formData.referralKickbackT1 || '',
      referralKickbackT2: formData.referralKickbackT2 || '',
      referralKickbackT3: formData.referralKickbackT3 || '',
      distributionChain: formData.distributionChain || '',
      depositChain: formData.depositChain || '',
      depositChainStablecoin: formData.depositChainStablecoin || '',
      referralGeneralDiscount: (formData as any).referralGeneralDiscount ?? '',
      referralGeneralKickback: (formData as any).referralGeneralKickback ?? '',
      referralCustomCsv: (formData as any).referralCustomCsv || null,
      projectTokenAddress: (formData as any).projectTokenAddress || '',
      projectTokenSymbol: (formData as any).projectTokenSymbol || '',
      projectTokenName: (formData as any).projectTokenName || '',
      projectTokenDecimals: (formData as any).projectTokenDecimals || '',
      lockdropDuration: (formData as any).lockdropDuration || '',
      lockdropStartDate: (formData as any).lockdropStartDate || '',
      lockdropEndDate: (formData as any).lockdropEndDate || '',
      targetTVL: (formData as any).targetTVL || '',
      targetYieldProposal: (formData as any).targetYieldProposal || '',
      fdvLastRound: (formData as any).fdvLastRound || '',
      withdrawalType:
        typeof (formData as any).withdrawalType === 'number'
          ? (formData as any).withdrawalType
          : 0,
    }

    if (isFullTgeRelease(initialState.tgeRelease)) {
      initialState.cliff = '0'
      initialState.linearVesting = '0'
    }

    return initialState
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [referralErrors, setReferralErrors] = useState<{
    customCsv?: string
  }>({})
  const [referralInvalidCount, setReferralInvalidCount] = useState(0)
  const [referralDuplicateCount, setReferralDuplicateCount] = useState(0)
  const [whitelistInvalidCount, setWhitelistInvalidCount] = useState(0)
  const [whitelistDuplicateCount, setWhitelistDuplicateCount] = useState(0)

  const clearErrorByKey = (key: string) => {
    setErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  const allocationSensitiveFields = new Set([
    'minimumRaiseThreshold',
    'fdv',
    'tokenSupply',
    'pricingMethod',
  ])

  const dropdownRef = useRef<HTMLDivElement>(null)
  const autoCalcAnnouncementRef = useRef<HTMLDivElement>(null)

  const distributionChains = DISTRIBUTION_CHAINS
  const isPrivateSale = ((formData as any).salesType || '') === 'Private Sale'
  const isLockdrops = ((formData as any).salesType || '') === 'Lockdrops'
  const disableFixedVestingFields = isFullTgeRelease(localData.tgeRelease)
  const pricingMethodRadioIds = {
    fixed: 'pricing-method-fixed',
    flexible: 'pricing-method-flexible',
    bespoke: 'pricing-method-bespoke',
  } as const

  const POOL_NAME_OPTIONS = [
    {
      value: 'Standard Pool',
      label: 'Standard Pool',
      description:
        'Set a mid-range price and standard vesting (e.g., 12–18m). Best for balanced investors.',
    },
    {
      value: 'Early Unlock',
      label: 'Early Unlock',
      description:
        'Short vesting (0–6m) with a higher price. Attracts users who want fast access.',
    },
    {
      value: 'Long-Term Holder',
      label: 'Long-Term Holder',
      description:
        'Longer vesting (24–36m) with a lower price. Rewards long-term commitment.',
    },
    { value: '__custom__', label: 'Custom Pool Name', description: '' },
  ] as const

  const FIXED_POOL_NAME_OPTIONS = [
    { value: 'Genesis Pool', label: 'Genesis Pool' },
    { value: '__custom__', label: 'Custom Pool Name' },
  ] as const

  const getPoolTypeDescription = (poolName: string): string => {
    const option = POOL_NAME_OPTIONS.find((o) => o.value === poolName)
    return option?.description || ''
  }

  const clearReferralData = () => ({
    referralEnabled: false,
    referralDiscountT1: '',
    referralDiscountT2: '',
    referralDiscountT3: '',
    referralKickbackT1: '',
    referralKickbackT2: '',
    referralKickbackT3: '',
    referralGeneralDiscount: '',
    referralGeneralKickback: '',
    referralCustomCsv: null,
  })

  const clearWhitelistData = () => ({
    whitelistCsv: null,
    whitelistEntries: [],
    whitelistAddressCount: 0,
  })

  const clearTokenFields = () => ({
    projectTokenAddress: '',
    projectTokenSymbol: '',
    projectTokenName: '',
    projectTokenDecimals: '',
  })

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (localData.pricingMethod !== 'flexible') return
    if (localData.flexibleSchedules.length >= 2) return
    const optionNames = ['Standard Pool', 'Early Unlock', 'Long-Term Holder']
    setLocalData((prev) => {
      if (prev.pricingMethod !== 'flexible') return prev
      if (prev.flexibleSchedules.length >= 2) return prev

      const timestamp = Date.now()
      const updatedSchedules = [...prev.flexibleSchedules]
      const usedNames = new Set(
        updatedSchedules
          .map((s) => s.name)
          .filter((name): name is string => Boolean(name && name.trim())),
      )

      if (updatedSchedules.length === 0) {
        updatedSchedules.push({
          id: `${timestamp}`,
          name: optionNames[0],
          fdv: '',
          tokenPrice: '',
          cliff: '',
          tgeRelease: '',
          linearVesting: '',
        })
        usedNames.add(optionNames[0])
      } else {
        const first = updatedSchedules[0]
        if (!first.name || !optionNames.includes(first.name)) {
          const replacement =
            optionNames.find((name) => !usedNames.has(name)) || optionNames[0]
          updatedSchedules[0] = { ...first, name: replacement }
          usedNames.add(replacement)
        } else {
          usedNames.add(first.name)
        }
      }

      while (updatedSchedules.length < 2) {
        const nextName =
          optionNames.find((name) => !usedNames.has(name)) ||
          optionNames[optionNames.length - 1]
        usedNames.add(nextName)
        updatedSchedules.push({
          id: `${timestamp + updatedSchedules.length}`,
          name: nextName,
          fdv: '',
          tokenPrice: '',
          cliff: '',
          tgeRelease: '',
          linearVesting: '',
        })
      }

      return { ...prev, flexibleSchedules: updatedSchedules }
    })
  }, [localData.pricingMethod, localData.flexibleSchedules.length])

  useEffect(() => {
    if (!isPrivateSale) return
    setReferralErrors({})
    setReferralInvalidCount(0)
    setReferralDuplicateCount(0)
    setLocalData((prev) => {
      const hasReferralData =
        prev.referralEnabled ||
        prev.referralDiscountT1 ||
        prev.referralDiscountT2 ||
        prev.referralDiscountT3 ||
        prev.referralKickbackT1 ||
        prev.referralKickbackT2 ||
        prev.referralKickbackT3 ||
        prev.referralGeneralDiscount ||
        prev.referralGeneralKickback ||
        prev.referralCustomCsv
      if (!hasReferralData) return prev
      const cleared = { ...prev, ...clearReferralData() }
      onUpdate &&
        onUpdate({
          ...clearReferralData(),
          referralCustomEntries: [],
        } as any)
      return cleared
    })
  }, [isPrivateSale, onUpdate])

  useEffect(() => {
    if (!isLockdrops) return
    setLocalData((prev) => {
      const hasTokenData =
        prev.projectTokenAddress ||
        prev.projectTokenSymbol ||
        prev.projectTokenName ||
        prev.projectTokenDecimals
      if (!hasTokenData) return prev
      const cleared = { ...prev, ...clearTokenFields() }
      onUpdate && onUpdate(clearTokenFields() as any)
      return cleared
    })
  }, [isLockdrops, onUpdate])

  useEffect(() => {
    if (!isLockdrops) return
    const threshold = localData.minimumRaiseThreshold
    if (threshold === '0' || Number(threshold) === 0) {
      setLocalData((prev) => ({ ...prev, minimumRaiseThreshold: '' }))
      onUpdate && onUpdate({ minimumRaiseThreshold: '' } as any)
    }
  }, [isLockdrops, localData.minimumRaiseThreshold, onUpdate])

  useEffect(() => {
    if (!isLockdrops) return
    const maxRaise = localData.maxRaise
    if (maxRaise) {
      setLocalData((prev) => ({ ...prev, maxRaise: '' }))
      setErrors((prev) => ({ ...prev, maxRaise: '' }))
      onUpdate && onUpdate({ maxRaise: '' } as any)
    }
  }, [isLockdrops, localData.maxRaise, onUpdate])

  useEffect(() => {
    const formSalesType = (formData as any).salesType || ''
    setLocalData((prev) => {
      if (prev.salesType === formSalesType) return prev
      return { ...prev, salesType: formSalesType }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(formData as any).salesType])

  const parseWhitelistCsv = async (file: File) => {
    const { entries, invalid, duplicates, error } = await parseWhitelistCsvFile(
      file,
    )
    if (error) {
      setErrors((prev) => ({ ...prev, whitelistCsv: error }))
      updateField('whitelistCsv', null)
      setLocalData((prev) => ({
        ...prev,
        whitelistCsv: null,
        whitelistEntries: [],
        whitelistAddressCount: 0,
      }))
      onUpdate &&
        onUpdate({ whitelistEntries: [], whitelistAddressCount: 0 } as any)
      setWhitelistInvalidCount(0)
      setWhitelistDuplicateCount(0)
      return
    }
    setWhitelistInvalidCount(invalid)
    setWhitelistDuplicateCount(duplicates)
    onUpdate &&
      onUpdate({
        whitelistEntries: entries,
        whitelistAddressCount: entries.length,
        enableWhitelistAccess: true,
      } as any)
    setLocalData((prev) => ({
      ...prev,
      whitelistEntries: entries,
      whitelistAddressCount: entries.length,
      enableWhitelistAccess: true,
    }))
  }

  const downloadWhitelistedAddressTemplate = async () => {
    try {
      const response = await fetch('/csv/whitelisted_sample.csv')
      if (!response.ok) throw new Error('Failed to download template')
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'whitelisted_sample.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      const link = document.createElement('a')
      link.href = '/csv/whitelisted_sample.csv'
      link.download = 'whitelisted_sample.csv'
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const getTodayDateTimeString = (): string => {
    return new Date().toISOString().slice(0, 16)
  }

  const ensureUTCDefault = (fieldName: string) => {
    if (!(localData as any)[fieldName]) {
      updateField(fieldName, getTodayDateTimeString())
    }
  }

  const updateField = (field: string, value: any) => {
    if (field === 'enableWhitelistAccess') {
      setLocalData((prev) => {
        const newData = {
          ...prev,
          enableWhitelistAccess: value,
          whitelistEntries: value ? prev.whitelistEntries : [],
          whitelistCsv: value ? prev.whitelistCsv : null,
          whitelistAddressCount: value ? prev.whitelistAddressCount : 0,
        }
        if (errors[field])
          setErrors((prevErrors) => ({ ...prevErrors, [field]: '' }))
        onUpdate && onUpdate(newData)
        return newData
      })
      return
    }
    if (field === 'enableBuybackProtection') {
      setLocalData((prev) => {
        const newData = {
          ...prev,
          enableBuybackProtection: value,
          buybackPaymentToken: value ? prev.buybackPaymentToken : '',
          buybackPrice: value ? prev.buybackPrice : '',
          fundingWallet: value ? prev.fundingWallet : '',
          buybackStartDate: value ? prev.buybackStartDate : '',
          buybackEndDate: value ? prev.buybackEndDate : '',
        }
        if (errors[field])
          setErrors((prevErrors) => ({ ...prevErrors, [field]: '' }))
        onUpdate && onUpdate(newData)
        return newData
      })
      return
    }
    if (field === 'fundingWallet') {
      setLocalData((prev) => ({ ...prev, [field]: value }))
      if (errors[field])
        setErrors((prevErrors) => ({ ...prevErrors, [field]: '' }))
      onUpdate && onUpdate({ [field]: value } as any)
      return
    }
    setLocalData((prev) => {
      const newData = { ...prev, [field]: value }
      if (errors[field])
        setErrors((prevErrors) => ({ ...prevErrors, [field]: '' }))
      if (allocationSensitiveFields.has(field))
        clearErrorByKey('totalAllocation')
      onUpdate && onUpdate({ [field]: value })
      return newData
    })
  }

  const parseTokenSupply = (tokenSupply: string) => {
    if (!tokenSupply || tokenSupply.trim() === '') return null
    const cleanSupply = tokenSupply.trim()
    if (cleanSupply.includes('-') || cleanSupply.includes('–')) {
      const parts = cleanSupply
        .split(/[-–]/)
        .map((part) => Number.parseFloat(part.trim().replace(/[^0-9.]/g, '')))
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        const [min, max] = parts.sort((a, b) => a - b)
        return { type: 'range', min, max }
      }
    }
    const singleValue = Number.parseFloat(cleanSupply.replace(/[^0-9.]/g, ''))
    if (!isNaN(singleValue)) return { type: 'single', value: singleValue }
    return null
  }

  const calculateTokenPriceDisplay = () => {
    const fdvNum = Number.parseFloat(
      String(localData.fdv || '').replace(/[^0-9.]/g, ''),
    )
    const supplyData = parseTokenSupply(localData.tokenSupply)
    if (isNaN(fdvNum) || !supplyData || fdvNum === 0) return ''
    if (supplyData.type === 'single') {
      const price = fdvNum / supplyData.value
      return price.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8,
      })
    } else {
      const priceLow = fdvNum / supplyData.max
      const priceHigh = fdvNum / supplyData.min
      return `${priceLow.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8,
      })} – ${priceHigh.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8,
      })}`
    }
  }

  const calculateTotalAllocationTokens = (): string | null => {
    const fdvNum = Number.parseFloat(
      String(localData.fdv || '').replace(/[^0-9.]/g, ''),
    )
    const minRaiseNum = Number.parseFloat(
      String(localData.minimumRaiseThreshold || '').replace(/[^0-9.]/g, ''),
    )
    const supplyData = parseTokenSupply(localData.tokenSupply)
    if (
      isNaN(fdvNum) ||
      isNaN(minRaiseNum) ||
      !supplyData ||
      fdvNum === 0 ||
      minRaiseNum === 0
    )
      return null
    if (supplyData.type === 'single') {
      const tokenPrice = fdvNum / supplyData.value
      const totalAllocation = minRaiseNum / tokenPrice
      return totalAllocation.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    } else {
      const priceLow = fdvNum / supplyData.max
      const priceHigh = fdvNum / supplyData.min
      const minAllocation = minRaiseNum / priceHigh
      const maxAllocation = minRaiseNum / priceLow
      return `${minAllocation.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })} – ${maxAllocation.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`
    }
  }

  const handleFixedFDVChange = (value: number) => {
    setLocalData((prev) => {
      const newData = { ...prev, fdv: String(value) }
      if (prev.tokenSupply && prev.tokenSupply.trim() !== '') {
        const supplyData = parseTokenSupply(prev.tokenSupply)
        if (supplyData && (supplyData as any).value) {
          const newTokenPrice = value / (supplyData as any).value
          if (newTokenPrice) {
            ;(newData as any).tokenPrice = String(newTokenPrice)
            if (autoCalcAnnouncementRef.current)
              autoCalcAnnouncementRef.current.textContent = `Token price calculated: $${newTokenPrice.toFixed(
                2,
              )}`
          }
        }
      }
      onUpdate && onUpdate(newData)
      return newData
    })
    if (errors.fdv) setErrors((prev) => ({ ...prev, fdv: '' }))
    clearErrorByKey('totalAllocation')
  }

  const handleFixedTgeChange = (value: string) => {
    setLocalData((prev) => {
      const nextState = { ...prev, tgeRelease: value }
      if (isFullTgeRelease(value)) {
        nextState.cliff = '0'
        nextState.linearVesting = '0'
      }
      onUpdate && onUpdate(nextState)
      return nextState
    })
    if (errors.tgeRelease) setErrors((prev) => ({ ...prev, tgeRelease: '' }))
    if (isFullTgeRelease(value)) {
      if (errors.cliff) setErrors((prev) => ({ ...prev, cliff: '' }))
      if (errors.linearVesting)
        setErrors((prev) => ({ ...prev, linearVesting: '' }))
    }
  }

  const isValidPercentage = (value: string): boolean => {
    const num = Number.parseFloat(value)
    return !isNaN(num) && num >= 0 && num <= 100
  }

  const handlePricingMethodChange = (value: string) => {
    const optionNames = ['Standard Pool', 'Early Unlock', 'Long-Term Holder']
    setLocalData((prev) => {
      const newData = { ...prev, pricingMethod: value }
      if (value === 'flexible' && prev.flexibleSchedules.length < 2) {
        const timestamp = Date.now()
        const updatedSchedules = [...prev.flexibleSchedules]
        const usedNames = new Set(
          updatedSchedules
            .map((s) => s.name)
            .filter((name): name is string => Boolean(name && name.trim())),
        )

        if (updatedSchedules.length === 0) {
          updatedSchedules.push({
            id: `${timestamp}`,
            name: optionNames[0],
            fdv: '',
            tokenPrice: '',
            cliff: '',
            tgeRelease: '',
            linearVesting: '',
          })
          usedNames.add(optionNames[0])
        } else {
          const first = updatedSchedules[0]
          if (!first.name || !optionNames.includes(first.name)) {
            const replacement =
              optionNames.find((name) => !usedNames.has(name)) || optionNames[0]
            updatedSchedules[0] = { ...first, name: replacement }
            usedNames.add(replacement)
          } else {
            usedNames.add(first.name)
          }
        }

        while (updatedSchedules.length < 2) {
          const nextName =
            optionNames.find((name) => !usedNames.has(name)) ||
            optionNames[optionNames.length - 1]
          usedNames.add(nextName)
          updatedSchedules.push({
            id: `${timestamp + updatedSchedules.length}`,
            name: nextName,
            fdv: '',
            tokenPrice: '',
            cliff: '',
            tgeRelease: '',
            linearVesting: '',
          })
        }
        newData.flexibleSchedules = updatedSchedules
      }
      onUpdate && onUpdate(newData)
      return newData
    })
    clearErrorByKey('totalAllocation')
  }

  const addFlexibleSchedule = () => {
    if (localData.flexibleSchedules.length >= 3) return
    const usedNames = localData.flexibleSchedules
      .map((s) => s.name)
      .filter(
        (name): name is string =>
          Boolean(name) &&
          name !== '__custom__' &&
          POOL_NAME_OPTIONS.some((o) => o.value === name),
      )
    const availableOptions = POOL_NAME_OPTIONS.filter(
      (o) => !usedNames.includes(o.value),
    )
    const defaultName =
      availableOptions.length > 0 ? availableOptions[0].value : '__custom__'
    const newSchedule: FlexibleSchedule = {
      id: Date.now().toString(),
      name: defaultName,
      fdv: '',
      tokenPrice: '',
      cliff: '',
      tgeRelease: '',
      linearVesting: '',
    }
    setLocalData((prev) => {
      const next = {
        ...prev,
        flexibleSchedules: [...prev.flexibleSchedules, newSchedule],
      }
      onUpdate && onUpdate(next)
      return next
    })
  }

  // const isValidWallet = (addr: string) => /^0x[a-fA-F0-9]{40}$/.test(addr.trim())

  const parseReferralCsv = async (file: File) => {
    const { entries, invalid, duplicates, error } = await parseReferralCsvFile(
      file,
    )
    if (error) {
      setReferralErrors((prev) => ({ ...prev, customCsv: error }))
      setReferralInvalidCount(0)
      setReferralDuplicateCount(0)
      onUpdate && onUpdate({ referralCustomEntries: [] } as any)
      updateField('referralCustomCsv', null)
      return
    }
    setReferralInvalidCount(invalid)
    setReferralDuplicateCount(duplicates)
    setReferralErrors((prev) => ({ ...prev, customCsv: undefined }))
    onUpdate && onUpdate({ referralCustomEntries: entries } as any)
  }

  const removeFlexibleSchedule = (id: string) => {
    setLocalData((prev) => {
      if (prev.flexibleSchedules.length <= 2) return prev
      const next = {
        ...prev,
        flexibleSchedules: prev.flexibleSchedules.filter((s) => s.id !== id),
      }
      onUpdate && onUpdate(next)
      return next
    })
  }

  const updateFlexibleSchedule = (
    id: string,
    field: keyof FlexibleSchedule,
    value: any,
  ) => {
    if (field === 'enableBuybackProtection') {
      setLocalData((prev) => {
        const updatedSchedules = prev.flexibleSchedules.map((s) => {
          if (s.id !== id) return s
          return {
            ...s,
            enableBuybackProtection: value,
            buybackPaymentToken: value ? s.buybackPaymentToken : '',
            buybackPrice: value ? s.buybackPrice : '',
            fundingWallet: value ? s.fundingWallet : '',
            buybackStartDate: value ? s.buybackStartDate : '',
            buybackEndDate: value ? s.buybackEndDate : '',
          }
        })
        const next = {
          ...prev,
          flexibleSchedules: updatedSchedules,
        }
        onUpdate && onUpdate(next)
        return next
      })
      return
    }
    setLocalData((prev) => {
      const updatedSchedules = prev.flexibleSchedules.map((s) => {
        if (s.id !== id) return s
        const nextSchedule = { ...s, [field]: value }
        if (field === 'tgeRelease' && isFullTgeRelease(value)) {
          nextSchedule.cliff = '0'
          nextSchedule.linearVesting = '0'
        }
        return nextSchedule
      })
      const next = {
        ...prev,
        flexibleSchedules: updatedSchedules,
      }
      onUpdate && onUpdate(next)
      return next
    })
    const errorKey = `flexible-${id}-${field}`
    if (errors[errorKey])
      setErrors((prev) => ({
        ...prev,
        [errorKey]: '',
      }))
    if (field === 'fdv') clearErrorByKey('totalAllocation')
    if (field === 'tgeRelease' && isFullTgeRelease(value)) {
      setErrors((prev) => ({
        ...prev,
        [`flexible-${id}-cliff`]: '',
        [`flexible-${id}-linearVesting`]: '',
      }))
    }
  }

  const duplicateFlexibleSchedule = (id: string) => {
    if (localData.flexibleSchedules.length >= 3) return
    const schedule = localData.flexibleSchedules.find((s) => s.id === id)
    if (!schedule) return
    const usedNames = localData.flexibleSchedules
      .map((s) => s.name)
      .filter(
        (name): name is string =>
          Boolean(name) &&
          name !== '__custom__' &&
          POOL_NAME_OPTIONS.some((o) => o.value === name),
      )
    const availableOptions = POOL_NAME_OPTIONS.filter(
      (o) => !usedNames.includes(o.value),
    )
    const defaultName =
      availableOptions.length > 0 ? availableOptions[0].value : '__custom__'
    const cloned = {
      ...schedule,
      id: Date.now().toString(),
      name: defaultName,
    }
    setLocalData((prev) => {
      if (prev.flexibleSchedules.length >= 3) return prev
      const next = {
        ...prev,
        flexibleSchedules: [...prev.flexibleSchedules, cloned],
      }
      onUpdate && onUpdate(next)
      return next
    })
  }

  const handleFlexibleFDVChange = (scheduleId: string, value: number) => {
    setLocalData((prev) => {
      const newData = { ...prev }
      newData.flexibleSchedules = newData.flexibleSchedules.map((s) =>
        s.id === scheduleId ? { ...s, fdv: String(value) } : s,
      )
      onUpdate && onUpdate(newData)
      return newData
    })
    const errorKey = `flexible-${scheduleId}-fdv`
    if (errors[errorKey])
      setErrors((prev) => ({
        ...prev,
        [errorKey]: '',
      }))
    clearErrorByKey('totalAllocation')
  }

  const calculateFlexibleTokenPrice = (schedule: any): string => {
    const fdv = Number.parseFloat(
      String(schedule.fdv || '').replace(/[^0-9.]/g, ''),
    )
    const tokenSupply = localData.tokenSupply || ''
    if (!fdv || !tokenSupply) return ''
    const tokenSupplyStr = tokenSupply.replace(/[^0-9.,\-\s]/g, '')
    const isRange = tokenSupplyStr.includes('-')
    if (isRange) {
      const [lowStr, highStr] = tokenSupplyStr.split('-').map((s) => s.trim())
      const lowSupply = Number.parseFloat(lowStr.replace(/,/g, ''))
      const highSupply = Number.parseFloat(highStr.replace(/,/g, ''))
      if (isNaN(lowSupply) || isNaN(highSupply)) return ''
      const lowPrice = fdv / highSupply
      const highPrice = fdv / lowSupply
      return `${lowPrice.toFixed(8)} – ${highPrice.toFixed(8)}`
    }
    const supply = Number.parseFloat(tokenSupplyStr.replace(/,/g, ''))
    if (isNaN(supply)) return ''
    const tokenPrice = fdv / supply
    return `${tokenPrice.toFixed(8)}`
  }

  const calculateFlexibleTotalAllocationTokens = (): string => {
    const tokenSupply = localData.tokenSupply || ''
    const minRaise = Number.parseFloat(
      String(localData.minimumRaiseThreshold || '0').replace(/[^0-9.]/g, ''),
    )
    if (!tokenSupply || !minRaise) return '—'
    const tokenSupplyStr = tokenSupply.replace(/[^0-9.,\-\s]/g, '')
    const isRange = tokenSupplyStr.includes('-')
    let totalMin = 0
    let totalMax = 0
    let hasRange = false
    for (const s of localData.flexibleSchedules) {
      const fdv = Number.parseFloat(
        String(s.fdv || '0').replace(/[^0-9.]/g, ''),
      )
      if (!fdv) continue
      if (isRange) {
        const [lowStr, highStr] = tokenSupplyStr.split('-').map((x) => x.trim())
        const low = Number.parseFloat(lowStr.replace(/,/g, ''))
        const high = Number.parseFloat(highStr.replace(/,/g, ''))
        if (isNaN(low) || isNaN(high)) continue
        const priceMin = fdv / high
        const priceMax = fdv / low
        const allocMin = minRaise / priceMax
        const allocMax = minRaise / priceMin
        totalMin += allocMin
        totalMax += allocMax
        hasRange = true
      } else {
        const supply = Number.parseFloat(tokenSupplyStr.replace(/,/g, ''))
        if (isNaN(supply)) continue
        const price = fdv / supply
        totalMin += minRaise / price
        totalMax += minRaise / price
      }
    }
    if (totalMin === 0) return '—'
    return hasRange && Math.abs(totalMax - totalMin) > 1
      ? `${totalMin.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })} – ${totalMax.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}`
      : totalMin.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
  }

  const inputErrorClass = (field: string) =>
    errors[field] ? 'border-red-500' : ''

  const minimumRaiseError = errors.minimumRaiseThreshold || undefined

  const handleSubmit = () => {
    const { errors: newErrors, isValid } = validateInvestmentTerms(localData)
    setErrors(newErrors)
    if (isValid) {
      onValidationErrors?.([])
      onValidationError?.('')
      onComplete(localData)
    } else {
      const fieldLabels: Record<string, string> = {
        depositChain: 'Deposit Chain',
        depositChainStablecoin: 'Deposit Chain Stablecoin',
        distributionChain: 'Distribution Chain',
        pricingMethod: 'Pricing Method',
        fixedPoolName: 'Pool Name',
        projectTokenAddress: 'Project Token Address',
        projectTokenSymbol: 'Token Symbol',
        projectTokenName: 'Token Name',
        projectTokenDecimals: 'Token Decimals',
        tokenSupply: 'Total Token Supply',
        minimumRaiseThreshold: 'Minimum Raise',
        minimumInvestmentPerUser: 'Minimum Investment Per User',
        maxRaise: 'Max Raise',
        fdv: 'FDV',
        tgeRelease: 'TGE Release',
        cliff: 'Cliff Period',
        linearVesting: 'Linear Vesting',
        totalAllocation: 'Total Allocation',
        flexible: 'Flexible Pricing Options',
        buybackPaymentToken: 'Buyback Payment Token',
        buybackPrice: 'Buyback Price',
        fundingWallet: 'Funding Wallet',
        buybackStartDate: 'Buyback Start Date',
        buybackEndDate: 'Buyback End Date',
        lockdropDuration: 'Lockdrop Duration',
        lockdropStartDate: 'Lockdrop Start Date',
        lockdropEndDate: 'Lockdrop End Date',
        targetTVL: 'Target TVL',
        targetYieldProposal: 'Target Yield Proposal',
        fdvLastRound: 'FDV of Last Round',
      }

      ;(localData.flexibleSchedules || []).forEach((schedule, index) => {
        const prefix = `flexible-${schedule.id}`
        fieldLabels[`${prefix}-buybackPaymentToken`] = `Flexible Pool ${
          index + 1
        } - Buyback Payment Token`
        fieldLabels[`${prefix}-buybackPrice`] = `Flexible Pool ${
          index + 1
        } - Buyback Price`
        fieldLabels[`${prefix}-fundingWallet`] = `Flexible Pool ${
          index + 1
        } - Funding Wallet`
        fieldLabels[`${prefix}-buybackStartDate`] = `Flexible Pool ${
          index + 1
        } - Buyback Start Date`
        fieldLabels[`${prefix}-buybackEndDate`] = `Flexible Pool ${
          index + 1
        } - Buyback End Date`
      })

      const mappedErrors = Object.keys(newErrors).map((id) => {
        let label = fieldLabels[id] || id
        if (id.startsWith('flexible-')) {
          const parts = id.split('-')
          const field = parts[parts.length - 1]
          const scheduleId = parts.slice(1, -1).join('-')
          const index = (localData.flexibleSchedules || []).findIndex(
            (s) => s.id === scheduleId,
          )

          const fieldMap: Record<string, string> = {
            name: 'Name',
            fdv: 'FDV',
            tgeRelease: 'TGE Release',
            cliff: 'Cliff Period',
            linearVesting: 'Linear Vesting',
            buybackPaymentToken: 'Buyback Payment Token',
            buybackPrice: 'Buyback Price',
            fundingWallet: 'Funding Wallet',
            buybackStartDate: 'Buyback Start Date',
            buybackEndDate: 'Buyback End Date',
          }
          const fieldLabel = fieldMap[field] || field
          label = `Flexible Pool ${index + 1} ${fieldLabel}`
        }
        return { id, label }
      })

      if (onValidationErrors) {
        onValidationErrors(mappedErrors)
      } else {
        onValidationError?.(
          'Please complete all required fields before continuing.',
        )
      }
    }
  }

  return (
    <div className="space-y-6">
      <div
        ref={autoCalcAnnouncementRef}
        aria-live="polite"
        className="sr-only"
      ></div>
      <Card>
        <CardHeader>
          <CardTitle>Basic Investment Terms</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {!isLockdrops && (
            <>
              <div className="space-y-2">
                <Label htmlFor="projectTokenAddress">
                  Project Token Address
                </Label>
                <Input
                  id="projectTokenAddress"
                  value={(localData as any).projectTokenAddress}
                  onChange={(e) =>
                    updateField('projectTokenAddress', e.target.value)
                  }
                  placeholder="0x..."
                  className={inputErrorClass('projectTokenAddress')}
                />
                <div className="flex flex-col gap-1">
                  {errors.projectTokenAddress && (
                    <p className="text-status-error text-xs inline-flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.projectTokenAddress}
                    </p>
                  )}
                  {requestChanges?.projectTokenAddress && (
                    <p className="text-status-error text-xs inline-flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {requestChanges.projectTokenAddress}
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="projectTokenSymbol">Symbol</Label>
                  <Input
                    id="projectTokenSymbol"
                    value={(localData as any).projectTokenSymbol}
                    onChange={(e) =>
                      updateField('projectTokenSymbol', e.target.value)
                    }
                    placeholder="e.g., ABC"
                    className={inputErrorClass('projectTokenSymbol')}
                  />
                  <div className="flex flex-col gap-1">
                    {errors.projectTokenSymbol && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.projectTokenSymbol}
                      </p>
                    )}
                    {requestChanges?.projectTokenSymbol && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.projectTokenSymbol}
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="projectTokenName">Name</Label>
                  <Input
                    id="projectTokenName"
                    value={(localData as any).projectTokenName}
                    onChange={(e) =>
                      updateField('projectTokenName', e.target.value)
                    }
                    placeholder="e.g., Alpha Token"
                    className={inputErrorClass('projectTokenName')}
                  />
                  <div className="flex flex-col gap-1">
                    {errors.projectTokenName && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.projectTokenName}
                      </p>
                    )}
                    {requestChanges?.projectTokenName && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.projectTokenName}
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="projectTokenDecimals">Decimals</Label>
                  <div
                    className={
                      errors.projectTokenDecimals
                        ? 'border border-status-error rounded-md'
                        : ''
                    }
                  >
                    <DropdownSelect
                      value={(localData as any).projectTokenDecimals || ''}
                      onChange={(val) =>
                        updateField('projectTokenDecimals', val)
                      }
                      options={[
                        { value: '6', label: '6' },
                        { value: '18', label: '18' },
                      ]}
                      placeholder="Select decimals"
                      ariaLabel="Select project token decimals"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    {errors.projectTokenDecimals && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.projectTokenDecimals}
                      </p>
                    )}
                    {requestChanges?.projectTokenDecimals && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.projectTokenDecimals}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="space-y-2">
            <Label htmlFor="tokenSupply">Total Token Supply</Label>
            <Input
              id="tokenSupply"
              type="number"
              min="0"
              step="1"
              value={localData.tokenSupply}
              onChange={(e) => updateField('tokenSupply', e.target.value)}
              placeholder="e.g., 10,000,000"
              required
              className={inputErrorClass('tokenSupply')}
            />
            <p className="text-xs text-neutral-400">
              Enter the total number of tokens in circulation.
            </p>
            <div className="flex flex-col gap-1">
              {errors.tokenSupply && (
                <p className="text-status-error text-xs inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.tokenSupply}
                </p>
              )}
              {requestChanges?.tokenSupply && (
                <p className="text-status-error text-xs inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {requestChanges.tokenSupply}
                </p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="minimumRaiseThreshold">
              Minimum Raise Threshold
              {isLockdrops && (
                <span className="text-neutral-400 ml-1">(Optional)</span>
              )}
            </Label>
            <InputUSD
              id="minimumRaiseThreshold"
              decimalScale={0}
              value={localData.minimumRaiseThreshold}
              onChange={(value) => {
                const numValue = Number(value)
                if (
                  isLockdrops &&
                  (value === null || value === undefined || numValue === 0)
                ) {
                  updateField('minimumRaiseThreshold', '')
                } else {
                  updateField('minimumRaiseThreshold', value)
                }
              }}
              placeholder="e.g., 20,000"
              error={minimumRaiseError || requestChanges?.minimumRaiseThreshold}
            />
            {!isLockdrops && (
              <div className="text-xs text-neutral-500 space-y-1">
                <p className="text-neutral-400">
                  • Minimum raise must be at least $20,000 to cover entity setup
                  {isPrivateSale
                    ? ' and annual SAFT management (approx. $6.5k/year).'
                    : '.'}
                </p>
                <p className="text-neutral-400">
                  • If the minimum is met, entity setup proceeds; if not, all
                  deposits are refunded.
                </p>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="minimumInvestmentPerUser">
              Minimum Investment Per User
            </Label>
            <InputUSD
              id="minimumInvestmentPerUser"
              decimalScale={0}
              value={localData.minimumInvestmentPerUser}
              onChange={(value) =>
                updateField('minimumInvestmentPerUser', value)
              }
              placeholder="100"
              error={
                errors.minimumInvestmentPerUser ||
                requestChanges?.minimumInvestmentPerUser
              }
            />
            <div className="flex flex-col gap-1">
              {requestChanges?.minimumInvestmentPerUser && (
                <p className="text-status-error text-xs inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {requestChanges.minimumInvestmentPerUser}
                </p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2">Deposit Chain</Label>
            <DropdownSelect
              id="depositChain"
              value={localData.depositChain || ''}
              onChange={(val) => {
                updateField('depositChain', val)
                updateField('depositChainStablecoin', '')
              }}
              options={distributionChains.map((c) => ({ value: c, label: c }))}
              placeholder="Select deposit chain"
              error={errors.depositChain}
              ariaLabel="Select deposit chain"
            />
            <div className="flex flex-col gap-1">
              {requestChanges?.depositChain && (
                <p className="text-status-error text-xs inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {requestChanges.depositChain}
                </p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              Deposit Chain Stablecoin
            </Label>
            <DropdownSelect
              id="depositChainStablecoin"
              value={localData.depositChainStablecoin || ''}
              onChange={(val) => updateField('depositChainStablecoin', val)}
              options={(
                DEPOSIT_CHAIN_STABLECOINS[localData.depositChain] || []
              ).map((s) => ({ value: s.value, label: s.label }))}
              placeholder="Select stablecoin"
              error={errors.depositChainStablecoin}
              ariaLabel="Select stablecoin for deposits"
              disabled={!localData.depositChain}
            />
            <div className="flex flex-col gap-1">
              {requestChanges?.depositChainStablecoin && (
                <p className="text-status-error text-xs inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {requestChanges.depositChainStablecoin}
                </p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              Distribution Chain
            </Label>
            <DropdownSelect
              id="distributionChain"
              value={localData.distributionChain || ''}
              onChange={(val) => updateField('distributionChain', val)}
              options={distributionChains.map((c) => ({ value: c, label: c }))}
              placeholder="Select distribution chain"
              error={errors.distributionChain}
              ariaLabel="Select distribution chain"
            />
            <div className="flex flex-col gap-1">
              {requestChanges?.distributionChain && (
                <p className="text-status-error text-xs inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {requestChanges.distributionChain}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {!isLockdrops && (
        <Card>
          <CardHeader>
            <CardTitle>Withdrawal Type</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-neutral-500">
              Select how investors can withdraw funds from this deal.
            </p>
            {[
              {
                value: 0,
                label: 'Standard (0)',
                description: 'Default withdrawal behavior.',
              },
              {
                value: 1,
                label: 'Refundable (1)',
                description: 'Investors may withdraw under specific conditions.',
              },
              {
                value: 2,
                label: 'Custom (2)',
                description: 'Custom withdrawal logic configured off-form.',
              },
            ].map((opt) => {
              const id = `withdrawalType-${opt.value}`
              const isSelected =
                Number((localData as any).withdrawalType ?? 0) === opt.value
              return (
                <div
                  key={opt.value}
                  className={`border rounded-lg p-4 transition-colors ${
                    isSelected
                      ? 'border-brand-primary bg-accent-purple/10'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      id={id}
                      type="radio"
                      name="withdrawalType"
                      value={opt.value}
                      checked={isSelected}
                      onChange={() =>
                        updateField('withdrawalType', opt.value)
                      }
                      className="mt-1 h-4 w-4 border-brand-primary text-brand-primary focus:ring-brand-primary accent-brand-primary"
                    />
                    <label htmlFor={id} className="flex-1 cursor-pointer">
                      <div className="font-medium text-neutral-950">
                        {opt.label}
                      </div>
                      {isSelected && (
                        <div className="text-sm text-neutral-600 mt-1">
                          {opt.description}
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              )
            })}
            {requestChanges?.withdrawalType && (
              <p className="text-status-error text-xs inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {requestChanges.withdrawalType}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {isLockdrops && (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl font-medium text-neutral-800">
              Lockdrop Deal Terms
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="lockdropDuration">Lockdrop Duration</Label>
              <DropdownSelect
                id="lockdropDuration"
                value={localData.lockdropDuration || ''}
                onChange={(value) => updateField('lockdropDuration', value)}
                options={[
                  { value: '3months', label: '3 months' },
                  { value: '6months', label: '6 months' },
                  { value: 'custom', label: 'Customize' },
                ]}
                placeholder="Select lockdrop duration"
                ariaLabel="Select lockdrop duration"
                error={
                  errors.lockdropDuration || requestChanges?.lockdropDuration
                }
              />
            </div>

            {localData.lockdropDuration === 'custom' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lockdropStartDate">Start Date (UTC)</Label>
                  <Input
                    id="lockdropStartDate"
                    type="datetime-local"
                    value={localData.lockdropStartDate}
                    onChange={(e) =>
                      updateField('lockdropStartDate', e.target.value)
                    }
                    className={inputErrorClass('lockdropStartDate')}
                  />
                  <div className="flex flex-col gap-1">
                    {errors.lockdropStartDate && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.lockdropStartDate}
                      </p>
                    )}
                    {requestChanges?.lockdropStartDate && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.lockdropStartDate}
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lockdropEndDate">End Date (UTC)</Label>
                  <Input
                    id="lockdropEndDate"
                    type="datetime-local"
                    value={localData.lockdropEndDate}
                    onChange={(e) =>
                      updateField('lockdropEndDate', e.target.value)
                    }
                    className={inputErrorClass('lockdropEndDate')}
                  />
                  <div className="flex flex-col gap-1">
                    {errors.lockdropEndDate && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.lockdropEndDate}
                      </p>
                    )}
                    {requestChanges?.lockdropEndDate && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.lockdropEndDate}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="border-t border-neutral-200 my-4"></div>

            <div className="space-y-2">
              <Label htmlFor="targetTVL">Target Total Value Locked (TVL)</Label>
              <InputUSD
                id="targetTVL"
                value={localData.targetTVL}
                onChange={(value) => updateField('targetTVL', value)}
                placeholder="Enter target TVL"
                error={errors.targetTVL || requestChanges?.targetTVL}
                className={inputErrorClass('targetTVL')}
              />
            </div>

            <div className="space-y-1">
              <div>
                <Label htmlFor="targetYieldProposal">
                  Target Yield Proposal
                </Label>
                <p className="text-xs text-neutral-600 mt-1">
                  Describe how the target yield is structured. Total = Base +
                  Incentives (e.g., "42% (10% Native yield + 32% $XYZ)")
                </p>
              </div>
              <Textarea
                id="targetYieldProposal"
                value={localData.targetYieldProposal}
                onChange={(e) =>
                  updateField('targetYieldProposal', e.target.value)
                }
                placeholder="Enter target yield proposal"
                rows={3}
                className={inputErrorClass('targetYieldProposal')}
              />
              <div className="flex flex-col gap-1">
                {errors.targetYieldProposal && (
                  <p className="text-status-error text-xs inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.targetYieldProposal}
                  </p>
                )}
                {requestChanges?.targetYieldProposal && (
                  <p className="text-status-error text-xs inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {requestChanges.targetYieldProposal}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="fdvLastRound">
                FDV of the last round to price the incentives
              </Label>
              <InputUSD
                id="fdvLastRound"
                value={localData.fdvLastRound}
                onChange={(value) => updateField('fdvLastRound', value)}
                placeholder="Enter FDV of last round"
                error={errors.fdvLastRound || requestChanges?.fdvLastRound}
                className={inputErrorClass('fdvLastRound')}
              />
            </div>

            <div className="font-medium text-neutral-800 -mb-3 leading-none">
              Whitelist Access
            </div>

            <OverviewWhitelistSection
              localData={{
                enableWhitelistAccess: localData.enableWhitelistAccess,
                whitelistCsv: localData.whitelistCsv,
                whitelistAddressCount: localData.whitelistAddressCount,
              }}
              errors={errors}
              downloadTemplate={downloadWhitelistedAddressTemplate}
              onWhitelistSelected={(file) => {
                updateField('whitelistCsv', file)
                if (file) {
                  parseWhitelistCsv(file)
                } else {
                  setLocalData((prev) => ({
                    ...prev,
                    ...clearWhitelistData(),
                  }))
                  onUpdate?.(clearWhitelistData() as any)
                }
              }}
              whitelistInvalidCount={whitelistInvalidCount}
              whitelistDuplicateCount={whitelistDuplicateCount}
              updateField={updateField}
              requestChanges={requestChanges}
              variant="inline"
            />
          </CardContent>
        </Card>
      )}

      {!isLockdrops && (
        <Card id="pricingMethod">
          <CardHeader>
            <CardTitle className="text-xl font-medium text-neutral-800">
              Pricing Method
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-neutral-100 rounded-lg p-4 mb-4 border border-neutral-200">
              <div className="font-medium text-neutral-800 mb-1">
                Allocation Logic: Bottom-Up Overflow (FFB)
              </div>
              <p className="text-sm text-neutral-600 mb-2">
                All allocation is filled progressively from smaller to larger
                contributions when the sale is oversubscribed.{' '}
                <a
                  href="https://impossiblefinance.notion.site/Fill-from-Bottom-FFB-2667cf9e26b880dfbc43e28ccf37caea"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-brand-primary hover:text-brand-hover inline-flex items-center gap-1"
                >
                  Learn more
                  <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            </div>
            <div
              className={`border rounded-lg p-4 transition-colors ${
                localData.pricingMethod === 'fixed'
                  ? 'border-brand-primary bg-accent-purple/10'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <div className="flex items-start gap-3">
                <input
                  id={pricingMethodRadioIds.fixed}
                  type="radio"
                  name="pricingMethod"
                  value="fixed"
                  checked={localData.pricingMethod === 'fixed'}
                  onChange={(e) => handlePricingMethodChange(e.target.value)}
                  className="mt-1 h-4 w-4 border-brand-primary text-brand-primary focus:ring-brand-primary accent-brand-primary"
                />
                <label
                  htmlFor={pricingMethodRadioIds.fixed}
                  className="flex-1 cursor-pointer"
                >
                  <div className="font-medium text-neutral-950">Fixed FDV</div>
                  {localData.pricingMethod === 'fixed' && (
                    <div className="text-sm text-neutral-600 mt-1">
                      One FDV and one vesting schedule apply to all buyers.
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    {requestChanges?.pricingMethod && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1 mt-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.pricingMethod}
                      </p>
                    )}
                  </div>
                  {localData.pricingMethod === 'fixed' && (
                    <div className="text-sm mt-2 font-semibold text-neutral-950">
                      Total Allocation:{' '}
                      {calculateTotalAllocationTokens() || '—'} tokens
                    </div>
                  )}
                  {localData.pricingMethod === 'fixed' &&
                    errors.totalAllocation && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1 mt-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.totalAllocation}
                      </p>
                    )}
                </label>
              </div>
              {localData.pricingMethod === 'fixed' && (
                <div className="mt-4 space-y-6">
                  <div className="border border-neutral-200 rounded-lg p-4 bg-white">
                    <div className="space-y-2">
                      <div className="space-y-2">
                        <Label
                          htmlFor="fixedPoolName"
                          className="text-sm font-normal"
                        >
                          Pool Name
                        </Label>
                        <DropdownSelect
                          id="fixedPoolName"
                          value={
                            FIXED_POOL_NAME_OPTIONS.some(
                              (o) => o.value === localData.fixedPoolName,
                            )
                              ? localData.fixedPoolName
                              : '__custom__'
                          }
                          onChange={(value) => {
                            if (value === '__custom__') {
                              updateField('fixedPoolName', '')
                            } else {
                              updateField('fixedPoolName', value)
                            }
                          }}
                          options={FIXED_POOL_NAME_OPTIONS.map((o) => ({
                            value: o.value,
                            label: o.label,
                          }))}
                          placeholder="Select pool name"
                        />
                        {!FIXED_POOL_NAME_OPTIONS.some(
                          (o) => o.value === localData.fixedPoolName,
                        ) && (
                          <div className="mt-1">
                            <Input
                              id="fixedPoolName"
                              value={localData.fixedPoolName}
                              onChange={(e) =>
                                updateField('fixedPoolName', e.target.value)
                              }
                              placeholder="Enter custom pool name"
                              className={cn(
                                'text-sm',
                                errors.fixedPoolName
                                  ? 'border-red-500 bg-red-50'
                                  : '',
                              )}
                            />
                            {errors.fixedPoolName && (
                              <p className="text-status-error text-xs inline-flex items-center gap-1 mt-1">
                                <AlertCircle className="w-3 h-3" />
                                {errors.fixedPoolName}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-6 mt-6">
                      <div className="space-y-2">
                        <Label className="text-base font-medium text-neutral-800">
                          Pricing
                        </Label>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label
                              htmlFor="fdv"
                              className="text-sm font-normal"
                            >
                              FDV
                            </Label>
                            <InputUSD
                              id="fdv"
                              decimalScale={0}
                              value={localData.fdv}
                              onChange={handleFixedFDVChange}
                              placeholder="e.g., 50,000,000"
                              error={errors.fdv || requestChanges?.fdv}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label
                              htmlFor="tokenPrice"
                              className="text-sm font-normal"
                            >
                              Token Price
                            </Label>
                            <div className="relative">
                              <Input
                                id="tokenPrice"
                                value={calculateTokenPriceDisplay()}
                                readOnly
                                disabled
                                aria-readonly="true"
                                className="!bg-neutral-50 text-neutral-600 pr-12"
                                tabIndex={-1}
                              />
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <span className="text-sm text-neutral-500">
                                  USD
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col gap-1">
                              {requestChanges?.tokenPrice && (
                                <p className="text-status-error text-xs inline-flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {requestChanges.tokenPrice}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-neutral-400">
                          Token price is calculated from FDV and token supply.
                          This field is read-only.
                        </p>
                      </div>
                      <div className="border-t border-neutral-200 my-6"></div>
                      <div className="space-y-2">
                        <Label
                          htmlFor="maxRaise"
                          className="text-base font-medium text-neutral-800"
                        >
                          Max Raise
                        </Label>
                        <InputUSD
                          id="maxRaise"
                          value={(localData as any).maxRaise}
                          onChange={(value) => updateField('maxRaise', value)}
                          placeholder="e.g., 1,000,000"
                          error={errors.maxRaise || requestChanges?.maxRaise}
                        />
                      </div>
                    </div>
                    <div className="border-t border-neutral-200 my-6"></div>
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <Label className="text-base font-medium text-neutral-800">
                          Vesting Schedule
                        </Label>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label
                              htmlFor="tgeRelease"
                              className="text-sm font-normal"
                            >
                              TGE release
                            </Label>
                            <div className="relative">
                              <Input
                                id="tgeRelease"
                                type="number"
                                placeholder="0"
                                min="0"
                                max="100"
                                value={localData.tgeRelease}
                                onChange={(e) =>
                                  handleFixedTgeChange(e.target.value)
                                }
                                required
                                className={cn(
                                  'pr-8',
                                  inputErrorClass('tgeRelease'),
                                )}
                              />
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <span className="text-sm text-neutral-500">
                                  %
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col gap-1">
                              {errors.tgeRelease && (
                                <p className="text-status-error text-xs inline-flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {errors.tgeRelease}
                                </p>
                              )}
                              {requestChanges?.tgeRelease && (
                                <p className="text-status-error text-xs inline-flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {requestChanges.tgeRelease}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label
                              htmlFor="cliff"
                              className="text-sm font-normal"
                            >
                              Cliff period
                            </Label>
                            <div className="relative">
                              <Input
                                id="cliff"
                                type="number"
                                placeholder="0"
                                min="0"
                                value={localData.cliff}
                                onChange={(e) => {
                                  const val = e.target.value
                                  setLocalData((prev) => ({
                                    ...prev,
                                    cliff: val,
                                  }))
                                  if (errors.cliff)
                                    setErrors((prev) => ({
                                      ...prev,
                                      cliff: '',
                                    }))
                                }}
                                disabled={disableFixedVestingFields}
                                className={cn(
                                  'pr-16',
                                  inputErrorClass('cliff'),
                                  disableFixedVestingFields &&
                                    'bg-neutral-50 text-neutral-500 cursor-not-allowed',
                                )}
                              />
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <span className="text-sm text-neutral-500">
                                  months
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col gap-1">
                              {errors.cliff && (
                                <p className="text-status-error text-xs inline-flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {errors.cliff}
                                </p>
                              )}
                              {requestChanges?.cliff && (
                                <p className="text-status-error text-xs inline-flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {requestChanges.cliff}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label
                              htmlFor="linearVesting"
                              className="text-sm font-normal"
                            >
                              Linear vesting period
                            </Label>
                            <div className="relative">
                              <Input
                                id="linearVesting"
                                type="number"
                                placeholder="0"
                                min="0"
                                value={localData.linearVesting}
                                onChange={(e) => {
                                  const val = e.target.value
                                  setLocalData((prev) => ({
                                    ...prev,
                                    linearVesting: val,
                                  }))
                                  if (errors.linearVesting)
                                    setErrors((prev) => ({
                                      ...prev,
                                      linearVesting: '',
                                    }))
                                }}
                                disabled={disableFixedVestingFields}
                                className={cn(
                                  'pr-16',
                                  inputErrorClass('linearVesting'),
                                  disableFixedVestingFields &&
                                    'bg-neutral-50 text-neutral-500 cursor-not-allowed',
                                )}
                              />
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <span className="text-sm text-neutral-500">
                                  months
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col gap-1">
                              {errors.linearVesting && (
                                <p className="text-status-error text-xs inline-flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {errors.linearVesting}
                                </p>
                              )}
                              {requestChanges?.linearVesting && (
                                <p className="text-status-error text-xs inline-flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {requestChanges.linearVesting}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {process.env.REACT_APP_ENV === 'staging' && (
                      <>
                        <div className="border-t border-neutral-200 my-6"></div>
                        <div className="space-y-4">
                          <Label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={localData.enableBuybackProtection}
                              onChange={(e) =>
                                updateField(
                                  'enableBuybackProtection',
                                  e.target.checked,
                                )
                              }
                              className="w-4 h-4 rounded border-neutral-300 text-brand-primary focus:ring-brand-primary accent-brand-primary"
                            />
                            Enable Buyback Protection
                          </Label>
                          {localData.enableBuybackProtection && (
                            <BuybackProtectionSection
                              localData={localData}
                              updateField={updateField}
                              errors={errors}
                              ensureUTCDefault={ensureUTCDefault}
                            />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="mt-6">
                    <OverviewWhitelistSection
                      localData={{
                        enableWhitelistAccess: localData.enableWhitelistAccess,
                        whitelistCsv: localData.whitelistCsv,
                        whitelistAddressCount: localData.whitelistAddressCount,
                      }}
                      errors={errors}
                      downloadTemplate={downloadWhitelistedAddressTemplate}
                      onWhitelistSelected={(file) => {
                        updateField('whitelistCsv', file)
                        if (file) {
                          parseWhitelistCsv(file)
                        } else {
                          setLocalData((prev) => ({
                            ...prev,
                            ...clearWhitelistData(),
                          }))
                          onUpdate?.(clearWhitelistData() as any)
                        }
                      }}
                      whitelistInvalidCount={whitelistInvalidCount}
                      whitelistDuplicateCount={whitelistDuplicateCount}
                      updateField={updateField}
                      requestChanges={requestChanges}
                    />
                  </div>
                </div>
              )}
            </div>

            <div
              id="flexible"
              className={`border rounded-lg p-4 transition-colors ${
                localData.pricingMethod === 'flexible'
                  ? 'border-brand-primary bg-accent-purple/10'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <div className="flex items-start gap-3">
                <input
                  id={pricingMethodRadioIds.flexible}
                  type="radio"
                  name="pricingMethod"
                  value="flexible"
                  checked={localData.pricingMethod === 'flexible'}
                  onChange={(e) => handlePricingMethodChange(e.target.value)}
                  className="mt-1 h-4 w-4 border-brand-primary text-brand-primary focus:ring-brand-primary accent-brand-primary"
                />
                <label
                  htmlFor={pricingMethodRadioIds.flexible}
                  className="flex-1 cursor-pointer"
                >
                  <div className="text-neutral-800 text-base font-medium">
                    Flexible FDV/Vesting
                  </div>
                  <div className="text-sm text-neutral-600 mt-1">
                    Multiple pricing and vesting options for different investor
                    tiers.&nbsp;
                    <a
                      href="https://www.notion.so/impossiblefinance/Flexible-FDV-Vesting-2407cf9e26b8801fa4fbccba7d97cc6a"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-primary hover:text-brand-hover underline"
                    >
                      Learn more
                    </a>
                  </div>
                  {localData.pricingMethod === 'flexible' && (
                    <div className="text-sm mt-2 font-semibold text-neutral-950">
                      Total Allocation:{' '}
                      {calculateFlexibleTotalAllocationTokens()} tokens
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    {localData.pricingMethod === 'flexible' &&
                      errors.totalAllocation && (
                        <p className="text-status-error text-xs inline-flex items-center gap-1 mt-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.totalAllocation}
                        </p>
                      )}
                  </div>
                </label>
              </div>
              {localData.pricingMethod === 'flexible' && (
                <div className="mt-4">
                  <div className="border border-neutral-200 rounded-lg p-4 bg-white mb-6">
                    <div className="space-y-2">
                      <Label
                        htmlFor="maxRaise"
                        className="text-base font-medium text-neutral-800"
                      >
                        Max Raise
                      </Label>
                      <InputUSD
                        id="maxRaise"
                        value={(localData as any).maxRaise}
                        onChange={(value) => updateField('maxRaise', value)}
                        placeholder="e.g., 1,000,000"
                        error={errors.maxRaise || requestChanges?.maxRaise}
                      />
                    </div>
                  </div>
                  {localData.flexibleSchedules.map((schedule, index) => {
                    const disableFlexibleVesting = isFullTgeRelease(
                      schedule.tgeRelease,
                    )
                    const isCustomPoolName = !POOL_NAME_OPTIONS.some(
                      (o) => o.value === schedule.name,
                    )
                    const usedPresetNames = localData.flexibleSchedules
                      .filter((s) => s.id !== schedule.id)
                      .map((s) => s.name)
                      .filter(
                        (name): name is string =>
                          Boolean(name) &&
                          name !== '__custom__' &&
                          POOL_NAME_OPTIONS.some((o) => o.value === name),
                      )
                    const availablePoolOptions = POOL_NAME_OPTIONS.filter(
                      (o) => !usedPresetNames.includes(o.value),
                    )
                    return (
                      <div
                        key={schedule.id}
                        className="border border-neutral-200 rounded-lg p-4 mb-6 bg-white"
                      >
                        <div className="flex flex-col gap-2">
                          <Label
                            htmlFor={`flexible-${schedule.id}-name`}
                            className="text-sm font-normal"
                          >
                            Pool Name
                          </Label>
                          <div className="flex flex-wrap items-center gap-3">
                            <div className="flex-1 min-w-[220px]">
                              <DropdownSelect
                                value={
                                  POOL_NAME_OPTIONS.some(
                                    (o) => o.value === schedule.name,
                                  )
                                    ? schedule.name
                                    : '__custom__'
                                }
                                onChange={(value) => {
                                  if (value === '__custom__') {
                                    updateFlexibleSchedule(
                                      schedule.id,
                                      'name',
                                      '',
                                    )
                                  } else {
                                    updateFlexibleSchedule(
                                      schedule.id,
                                      'name',
                                      value,
                                    )
                                  }
                                }}
                                options={availablePoolOptions.map((o) => ({
                                  value: o.value,
                                  label: o.label,
                                }))}
                                placeholder="Select pool name"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  duplicateFlexibleSchedule(schedule.id)
                                }
                                className="text-neutral-500 hover:text-brand-hover"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              {localData.flexibleSchedules.length > 2 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    removeFlexibleSchedule(schedule.id)
                                  }
                                  className="text-neutral-500 hover:text-brand-hover"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {isCustomPoolName && (
                            <div className="mt-1">
                              <Input
                                id={`flexible-${schedule.id}-name`}
                                value={schedule.name || ''}
                                onChange={(e) =>
                                  updateFlexibleSchedule(
                                    schedule.id,
                                    'name',
                                    e.target.value,
                                  )
                                }
                                placeholder="Enter custom pool name"
                                className={cn(
                                  'text-sm',
                                  inputErrorClass(
                                    `flexible-${schedule.id}-name`,
                                  ),
                                )}
                              />
                              {errors[`flexible-${schedule.id}-name`] && (
                                <p className="text-status-error text-xs inline-flex items-center gap-1 mt-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {errors[`flexible-${schedule.id}-name`]}
                                </p>
                              )}
                            </div>
                          )}
                          {schedule.name &&
                            schedule.name !== '__custom__' &&
                            !isCustomPoolName && (
                              <p className="text-xs text-neutral-500 mt-1">
                                {getPoolTypeDescription(schedule.name)}
                              </p>
                            )}
                          <div className="flex flex-col gap-1">
                            {(requestChanges?.[`flexible-${index}-name`] ||
                              requestChanges?.[`flexible-${index}`]) && (
                              <p className="text-status-error text-xs inline-flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {requestChanges[`flexible-${index}-name`] ||
                                  requestChanges[`flexible-${index}`]}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 space-y-6">
                          <div className="space-y-2">
                            <Label className="text-base font-medium text-neutral-800">
                              Pricing
                            </Label>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label
                                  htmlFor={`flexible-${schedule.id}-fdv`}
                                  className="text-sm font-normal"
                                >
                                  FDV
                                </Label>
                                <InputUSD
                                  id={`flexible-${schedule.id}-fdv`}
                                  value={schedule.fdv}
                                  onChange={(value) =>
                                    handleFlexibleFDVChange(schedule.id, value)
                                  }
                                  placeholder="e.g., 50,000,000"
                                  error={
                                    errors[`flexible-${schedule.id}-fdv`] ||
                                    requestChanges?.[`flexible-${index}-fdv`]
                                  }
                                />
                              </div>
                              <div className="space-y-2">
                                <Label
                                  htmlFor={`flexible-${schedule.id}-tokenPrice`}
                                  className="text-sm font-normal"
                                >
                                  Token Price
                                </Label>
                                <div className="relative">
                                  <div
                                    id={`flexible-${schedule.id}-tokenPrice`}
                                    className="h-10 px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-md flex items-center text-sm text-slate-400 pr-12"
                                  >
                                    {calculateFlexibleTokenPrice(schedule)}
                                  </div>
                                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <span className="text-sm text-neutral-500">
                                      USD
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <p className="text-xs text-neutral-400">
                              Token price is calculated from FDV and token
                              supply. This field is read-only.
                            </p>
                          </div>
                        </div>

                        <div className="border-t border-neutral-200 my-6"></div>

                        <div className="space-y-6">
                          <div className="space-y-2">
                            <Label className="text-base font-medium text-neutral-800">
                              Vesting Schedule
                            </Label>
                            <div className="grid grid-cols-3 gap-4">
                              <div className="space-y-2">
                                <Label
                                  htmlFor={`flexible-${schedule.id}-tgeRelease`}
                                  className="text-sm font-normal"
                                >
                                  TGE release
                                </Label>
                                <div className="relative">
                                  <Input
                                    id={`flexible-${schedule.id}-tgeRelease`}
                                    type="number"
                                    placeholder="0"
                                    min="0"
                                    max="100"
                                    value={schedule.tgeRelease}
                                    onChange={(e) =>
                                      updateFlexibleSchedule(
                                        schedule.id,
                                        'tgeRelease',
                                        e.target.value,
                                      )
                                    }
                                    required
                                    className={cn(
                                      'pr-8',
                                      schedule.tgeRelease &&
                                        !isValidPercentage(schedule.tgeRelease)
                                        ? 'border-red-500'
                                        : '',
                                      inputErrorClass(
                                        `flexible-${schedule.id}-tgeRelease`,
                                      ),
                                    )}
                                  />
                                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <span className="text-sm text-neutral-500">
                                      %
                                    </span>
                                  </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                  {errors[
                                    `flexible-${schedule.id}-tgeRelease`
                                  ] && (
                                    <p className="text-status-error text-xs inline-flex items-center gap-1">
                                      <AlertCircle className="w-3 h-3" />
                                      {
                                        errors[
                                          `flexible-${schedule.id}-tgeRelease`
                                        ]
                                      }
                                    </p>
                                  )}
                                  {requestChanges?.[
                                    `flexible-${index}-tgeRelease`
                                  ] && (
                                    <p className="text-status-error text-xs inline-flex items-center gap-1">
                                      <AlertCircle className="w-3 h-3" />
                                      {
                                        requestChanges[
                                          `flexible-${index}-tgeRelease`
                                        ]
                                      }
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label
                                  htmlFor={`flexible-${schedule.id}-cliff`}
                                  className="text-sm font-normal"
                                >
                                  Cliff period
                                </Label>
                                <div className="relative">
                                  <Input
                                    id={`flexible-${schedule.id}-cliff`}
                                    type="number"
                                    placeholder="0"
                                    min="0"
                                    value={schedule.cliff}
                                    onChange={(e) =>
                                      updateFlexibleSchedule(
                                        schedule.id,
                                        'cliff',
                                        e.target.value,
                                      )
                                    }
                                    disabled={disableFlexibleVesting}
                                    className={cn(
                                      'pr-16',
                                      inputErrorClass(
                                        `flexible-${schedule.id}-cliff`,
                                      ),
                                      disableFlexibleVesting &&
                                        'bg-neutral-50 text-neutral-500 cursor-not-allowed',
                                    )}
                                  />
                                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <span className="text-sm text-neutral-500">
                                      months
                                    </span>
                                  </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                  {errors[`flexible-${schedule.id}-cliff`] && (
                                    <p className="text-status-error text-xs inline-flex items-center gap-1">
                                      <AlertCircle className="w-3 h-3" />
                                      {errors[`flexible-${schedule.id}-cliff`]}
                                    </p>
                                  )}
                                  {requestChanges?.[
                                    `flexible-${index}-cliff`
                                  ] && (
                                    <p className="text-status-error text-xs inline-flex items-center gap-1">
                                      <AlertCircle className="w-3 h-3" />
                                      {
                                        requestChanges[
                                          `flexible-${index}-cliff`
                                        ]
                                      }
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label
                                  htmlFor={`flexible-${schedule.id}-linearVesting`}
                                  className="text-sm font-normal"
                                >
                                  Linear vesting period
                                </Label>
                                <div className="relative">
                                  <Input
                                    id={`flexible-${schedule.id}-linearVesting`}
                                    type="number"
                                    placeholder="0"
                                    min="0"
                                    value={schedule.linearVesting}
                                    onChange={(e) =>
                                      updateFlexibleSchedule(
                                        schedule.id,
                                        'linearVesting',
                                        e.target.value,
                                      )
                                    }
                                    disabled={disableFlexibleVesting}
                                    className={cn(
                                      'pr-16',
                                      inputErrorClass(
                                        `flexible-${schedule.id}-linearVesting`,
                                      ),
                                      disableFlexibleVesting &&
                                        'bg-neutral-50 text-neutral-500 cursor-not-allowed',
                                    )}
                                  />
                                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <span className="text-sm text-neutral-500">
                                      months
                                    </span>
                                  </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                  {errors[
                                    `flexible-${schedule.id}-linearVesting`
                                  ] && (
                                    <p className="text-status-error text-xs inline-flex items-center gap-1">
                                      <AlertCircle className="w-3 h-3" />
                                      {
                                        errors[
                                          `flexible-${schedule.id}-linearVesting`
                                        ]
                                      }
                                    </p>
                                  )}
                                  {requestChanges?.[
                                    `flexible-${index}-linearVesting`
                                  ] && (
                                    <p className="text-status-error text-xs inline-flex items-center gap-1">
                                      <AlertCircle className="w-3 h-3" />
                                      {
                                        requestChanges[
                                          `flexible-${index}-linearVesting`
                                        ]
                                      }
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="border-t border-neutral-200 my-6"></div>

                        <div className="space-y-4">
                          <Label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={
                                schedule.enableBuybackProtection || false
                              }
                              onChange={(e) =>
                                updateFlexibleSchedule(
                                  schedule.id,
                                  'enableBuybackProtection',
                                  e.target.checked,
                                )
                              }
                              className="w-4 h-4 rounded border-neutral-300 text-brand-primary focus:ring-brand-primary accent-brand-primary"
                            />
                            Enable Buyback Protection
                          </Label>
                          {schedule.enableBuybackProtection && (
                            <BuybackProtectionSection
                              localData={{
                                enableBuybackProtection:
                                  schedule.enableBuybackProtection || false,
                                buybackPaymentToken:
                                  schedule.buybackPaymentToken || '',
                                buybackPrice: schedule.buybackPrice || '',
                                fundingWallet: schedule.fundingWallet || '',
                                buybackStartDate:
                                  schedule.buybackStartDate || '',
                                buybackEndDate: schedule.buybackEndDate || '',
                              }}
                              updateField={(field, value) =>
                                updateFlexibleSchedule(
                                  schedule.id,
                                  field as keyof FlexibleSchedule,
                                  value,
                                )
                              }
                              errors={errors}
                              ensureUTCDefault={() => {}}
                              scheduleId={`flexible-${schedule.id}`}
                            />
                          )}
                        </div>
                      </div>
                    )
                  })}
                  {localData.flexibleSchedules.length < 3 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addFlexibleSchedule}
                      className="flex items-center gap-2 bg-transparent"
                    >
                      <Plus className="w-4 h-4" />
                      Add another Option
                    </Button>
                  )}

                  <div className="mt-6">
                    <OverviewWhitelistSection
                      localData={{
                        enableWhitelistAccess: localData.enableWhitelistAccess,
                        whitelistCsv: localData.whitelistCsv,
                        whitelistAddressCount: localData.whitelistAddressCount,
                      }}
                      errors={errors}
                      downloadTemplate={downloadWhitelistedAddressTemplate}
                      onWhitelistSelected={(file) => {
                        updateField('whitelistCsv', file)
                        if (file) {
                          parseWhitelistCsv(file)
                        } else {
                          setLocalData((prev) => ({
                            ...prev,
                            ...clearWhitelistData(),
                          }))
                          onUpdate?.(clearWhitelistData() as any)
                        }
                      }}
                      whitelistInvalidCount={whitelistInvalidCount}
                      whitelistDuplicateCount={whitelistDuplicateCount}
                      updateField={updateField}
                      requestChanges={requestChanges}
                    />
                  </div>
                </div>
              )}
            </div>

            <div
              className={`border rounded-lg p-4 transition-colors ${
                localData.pricingMethod === 'bespoke'
                  ? 'border-brand-primary bg-blue-50'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <div className="flex items-start gap-3">
                <input
                  id={pricingMethodRadioIds.bespoke}
                  type="radio"
                  name="pricingMethod"
                  value="bespoke"
                  checked={localData.pricingMethod === 'bespoke'}
                  onChange={(e) => handlePricingMethodChange(e.target.value)}
                  className="mt-1 w-4 h-4 text-brand-primary border-neutral-300 focus:ring-brand-primary"
                />
                <label
                  htmlFor={pricingMethodRadioIds.bespoke}
                  className="flex-1 cursor-pointer"
                >
                  <div className="font-medium text-neutral-950">
                    Request bespoke
                  </div>
                </label>
              </div>
              {localData.pricingMethod === 'bespoke' && (
                <div className="mt-4 space-y-4">
                  <Textarea
                    placeholder="Describe your custom pricing requirements..."
                    value={(localData as any).bespokeDescription}
                    onChange={(e) =>
                      updateField('bespokeDescription', e.target.value)
                    }
                    rows={3}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      {!isPrivateSale && (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">
              Referral System (optional)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="border border-neutral-200 rounded-lg p-4 bg-white">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-neutral-950">
                    General Public
                  </h3>
                  <p className="text-neutral-600 mt-1 text-xs">
                    Discounts apply to users who join using a referral code. Any
                    signed-in user can share their unique referral code for
                    public sales.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-normal">Discount (%)</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        placeholder="0"
                        value={(localData as any).referralGeneralDiscount}
                        onChange={(e) =>
                          updateField('referralGeneralDiscount', e.target.value)
                        }
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-neutral-600">
                        %
                      </span>
                    </div>
                    {requestChanges?.referralGeneralDiscount && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1 mt-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.referralGeneralDiscount}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-normal">Kickback (%)</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        placeholder="0"
                        value={(localData as any).referralGeneralKickback}
                        onChange={(e) =>
                          updateField('referralGeneralKickback', e.target.value)
                        }
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-neutral-600">
                        %
                      </span>
                    </div>
                    {requestChanges?.referralGeneralKickback && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1 mt-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.referralGeneralKickback}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-neutral-200 rounded-lg p-4 bg-white">
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-neutral-950">
                      Custom Wallets (KOLs / PRs)
                    </h3>
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-label="View referral CSV instructions"
                            className="text-neutral-500 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-400 rounded-full p-1"
                          >
                            <InfoIcon className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          align="start"
                          sideOffset={8}
                          className="max-w-[360px] rounded-md bg-white px-4 py-3 text-xs text-neutral-900 shadow-lg border border-neutral-200 space-y-2"
                        >
                          {CUSTOM_REFERRAL_TOOLTIP_LINES.map((line) => (
                            <p key={line}>{line}</p>
                          ))}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-neutral-600 mt-1 text-xs">
                    Upload a wallet list to assign specific discount and
                    kickback rates per wallet. This is typically used for PR and
                    KOL partners.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Upload CSV</Label>
                  <FileUpload
                    mode="document"
                    accept="text/csv,.csv"
                    buttonLabel="Upload CSV"
                    helperText="CSV only • Max 5 MB"
                    templateUrl="/csv/referral_sample.csv"
                    templateName="Custom Referral Template"
                    maxSizeMB={5}
                    errorStrings={{
                      fileCount: 'Please upload one file.',
                      fileType: 'CSV only. Please upload a .csv file.',
                      fileSize: 'File is too large (max 5 MB).',
                      unreadable:
                        "We couldn't read this CSV. Please try again.",
                      empty: 'The file appears to be empty.',
                      generic: 'Upload failed. Please try again.',
                    }}
                    ariaLabel="Upload custom wallets CSV file"
                    onFileSelect={(file) => {
                      updateField('referralCustomCsv', file)
                      if (file) parseReferralCsv(file)
                    }}
                    currentFile={(localData as any).referralCustomCsv}
                  />
                  <div className="flex flex-col gap-1">
                    <p className="text-xs text-neutral-500 mt-1">
                      {referralInvalidCount} invalid • {referralDuplicateCount}{' '}
                      duplicates
                    </p>
                    {referralErrors.customCsv && (
                      <p className="text-xs text-status-error inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {referralErrors.customCsv}
                      </p>
                    )}
                    {requestChanges?.referralCustomCsv && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.referralCustomCsv}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={onBack}
          size="md"
          className="flex items-center gap-2 bg-transparent px-5"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          variant="brand"
          size="md"
          className="flex items-center gap-2 px-5"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}
